<template>
  <w-modal
    v-model="show"
    class="modal-static-footer"
    close-button="Anuluj"
    submit-button="Zapisz"
    :title="title"
    :close-on-submit="false"
    :hide-on-overlay="false"
    :hide-on-esc="false"
    @submit="save"
    @close="$emit('close')"
  >
    <div class="row filter">
      <div v-for="(filter, index) in values" :key="'filter_' + index" class="mt-3" :class="`col-${filter.width || 12}`">
        <div v-if="filter.type === 'radio'">
          <div class="pb-2">
            <strong>
              {{ filter.title }}
            </strong>
          </div>

          <div class="d-flex">
            <div class="d-flex align-items-center flex-wrap">
              <div
                v-for="(option, optionIndex) in filter.options"
                :key="`filter_${index}_option_${optionIndex}`"
                class="custom-control custom-radio mr-3"
              >
                <input
                  :id="`filter_${index}_option_${optionIndex}`"
                  v-model="filter.value"
                  class="mr-2 custom-control-input"
                  type="radio"
                  :value="option.value"
                />

                <label class="custom-control-label" :for="`filter_${index}_option_${optionIndex}`">
                  {{ option.text }}
                </label>
              </div>
            </div>

            <w-button text="Wyczyść" class="btn-light" @click="filter.value = undefined" />
          </div>
        </div>

        <div v-if="filter.type === 'input'" class="form-group filter-input">
          <label :for="`filter_${index}`">
            <strong>
              {{ filter.title }}
            </strong>
          </label>

          <div>
            <input :id="`filter_${index}`" v-model="filter.value" class="form-control" :type="filter.valueType" />

            <w-button text="Wyczyść" class="btn-light" @click="filter.value = ''" />
          </div>
        </div>

        <div v-if="filter.type === 'regex'" class="form-group filter-input">
          <label :for="`filter_${index}`">
            <strong>
              {{ filter.title }}
            </strong>
          </label>

          <div>
            <input
              :id="`filter_${index}`"
              v-model="filter.value.$regex"
              class="form-control"
              @input="updateRegexFilter(filter)"
            />

            <w-button text="Wyczyść" class="btn-light" @click="filter.value = {}" />
          </div>
        </div>

        <div v-if="filter.type === 'arrayRegex'" class="form-group filter-input">
          <label :for="`filter_${index}`">
            <strong>
              {{ filter.title }}
            </strong>
          </label>

          <div>
            <input
              :id="`filter_${index}`"
              v-model="filter.model"
              class="form-control"
              @input="updateArrayRegexFilter(filter)"
            />

            <w-button text="Wyczyść" class="btn-light" @click="clearArrayRegexFilter(filter)" />
          </div>
        </div>

        <div v-if="filter.type === 'date'" class="filter-date">
          <no-schema-datepicker
            v-if="filter.nestedField"
            v-model="filter.value[filter.nestedField]"
            :title="filter.title"
            :options="filter"
          />

          <no-schema-datepicker v-else v-model="filter.value" :title="filter.title" :options="filter" />
        </div>

        <div v-if="filter.type === 'dateRange'" class="filter-daterange">
          <div class="pb-2">
            <strong>
              {{ filter.title }}
            </strong>
          </div>

          <div class="d-flex">
            <no-schema-datepicker v-model="filter.value.$gte" title="Od" class="mr-3" />

            <no-schema-datepicker v-model="filter.value.$lte" title="Do" class="mr-3" :options="endDateOptions" />

            <div class="clear-button-container">
              <w-button text="Wyczyść" class="btn-light" @click="filter.value = {}" />
            </div>
          </div>
        </div>

        <div v-if="filter.type === 'numberRange'" class="filter-numberrange form-group">
          <div class="pb-2">
            <strong>
              {{ filter.title }}
            </strong>
          </div>

          <div class="d-flex mb-3">
            <div class="mr-3">
              <label :for="`filter_${index}_1`">Od</label>

              <input
                :id="`filter_${index}_1`"
                v-model.number="filter.value.$gte"
                type="number"
                class="form-control ml-2"
                :min="filter.min"
                :max="filter.max"
              />
            </div>

            <div class="mr-3">
              <label :for="`filter_${index}_2`">Do</label>

              <input
                :id="`filter_${index}_2`"
                v-model.number="filter.value.$lte"
                type="number"
                class="form-control ml-2"
                :min="filter.min"
                :max="filter.max"
              />
            </div>

            <w-button text="Wyczyść" class="btn-light" @click="filter.value = {}" />
          </div>
        </div>

        <div v-if="filter.type === 'autocomplete'" class="form-group">
          <label :for="`filter_${index}`">
            <strong>
              {{ filter.title }}
            </strong>
          </label>

          <div class="row">
            <no-schema-autocomplete
              v-model="filter.value"
              class="col-10 col-md-6"
              :options="filter"
              :input-id="`filter_${index}`"
            />

            <div class="col-2">
              <w-button text="Wyczyść" class="btn-light" @click="filter.value = undefined" />
            </div>
          </div>
        </div>

        <div v-if="filter.type === 'multiselect'" class="form-group">
          <label :for="`filter_${index}`">
            <strong>
              {{ filter.title }}
            </strong>
          </label>

          <div class="row">
            <no-schema-multiselect
              v-model="filter.value.$in"
              class="col-10 col-md-6"
              :options="filter"
              :input-id="`filter_${index}`"
            />

            <div class="col-2">
              <w-button text="Wyczyść" class="btn-light" @click="filter.value.$in = []" />
            </div>
          </div>
        </div>

        <div v-if="filter.type === 'checkbox'">
          <div class="pb-2">
            <strong>
              {{ filter.title }}
            </strong>
          </div>

          <div
            v-for="(option, optionIndex) in filter.options"
            :key="`filter_${index}_option_${optionIndex}`"
            class="custom-control custom-checkbox mb-2"
          >
            <input
              :id="`filter_${index}_option_${optionIndex}`"
              v-model="filter.value"
              class="mr-2 custom-control-input"
              type="checkbox"
              :value="option.value"
            />

            <label class="custom-control-label" :for="`filter_${index}_option_${optionIndex}`">{{ option.text }}</label>
          </div>
        </div>
      </div>
    </div>
  </w-modal>
</template>

<script>
import NoSchemaAutocomplete from '@/components/formComponents/NoSchemaAutocomplete';
import NoSchemaDatepicker from '@/components/formComponents/NoSchemaDatepicker';
import NoSchemaMultiselect from '@/components/formComponents/NoSchemaMultiselect';
import { modalMixin } from '@/mixins/modal';

// @vuese
// Modal służący do wyboru listy elementów za pomocą checkboxów
export default {
  components: { NoSchemaAutocomplete, NoSchemaDatepicker, NoSchemaMultiselect },
  mixins: [modalMixin],
  props: {
    // Tytuł modala
    title: { type: String, required: false },
    // Opcje do wyboru
    filters: { type: Array, required: true }
  },
  data() {
    return { endDateOptions: { manipulateDate: { add: [1, 'day'] } }, values: [] };
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.values = JSON.parse(JSON.stringify(this.filters));
      }
    }
  },
  methods: {
    // @vuese
    // Czyści filtr typu arrayRegex
    clearArrayRegexFilter(filter) {
      filter.model = '';
      filter.value = {};
    },

    // @vuese
    // Zapisuje wybrane kolumny
    save() {
      this.$emit('save', this.values);
    },

    // @vuese
    // Aktualizuje filtr typu arrayRegex
    updateArrayRegexFilter(filter) {
      if (filter.model) {
        filter.value = { $elemMatch: { [filter.fieldName]: { $regex: filter.model, $options: 'i' } } };
      } else {
        filter.value = { $elemMatch: { [filter.fieldName]: { $regex: filter.model } } };
      }
    },

    // @vuese
    // Aktualizuje filtr typu regex
    updateRegexFilter(filter) {
      if (filter.value.$regex) {
        filter.value = { ...filter.value, $options: 'i' };
      } else {
        filter.value = { $regex: filter.value.$regex };
      }
    }
  }
};
</script>

<style>
.filter strong {
  font-weight: bold;
}

.filter-input input {
  width: 300px;
  display: inline-block;
  margin-right: 10px;
}

.filter-input button {
  margin-top: -5px;
}

.filter-daterange .clear-button-container {
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
}

.filter-numberrange input {
  width: 300px;
  display: inline-block;
  margin-right: 10px;
}
</style>
